import React from 'react';
import { Paper, Typography, Box } from '@mui/material';

interface QuoteCardProps {
  quote: {
    quote: string;
    author: string;
  };
  colorIndex?: number;
}

// Array of color themes - each with a background and accent color
const colorThemes = [
  { bg: '#336076', accent: '#FFD700' },   // Original blue with gold
  { bg: '#2D5744', accent: '#FF9B9B' },   // Forest green with coral
  { bg: '#614B79', accent: '#98FF98' },   // Purple with mint
  { bg: '#855A40', accent: '#87CEEB' },   // Warm brown with sky blue
  { bg: '#76424E', accent: '#98FB98' },   // Burgundy with pale green
  { bg: '#364B59', accent: '#FFA07A' },   // Navy with light salmon
  { bg: '#6B4423', accent: '#ADD8E6' },   // Deep brown with light blue
  { bg: '#4A5D46', accent: '#FFB6C1' }    // Olive with light pink
];

const QuoteCard: React.FC<QuoteCardProps> = ({ quote, colorIndex }) => {
  // Use provided index or generate random one
  const themeIndex = colorIndex ?? Math.floor(Math.random() * colorThemes.length);
  const theme = colorThemes[themeIndex];

  // Calculate responsive font size based on quote length
  const getQuoteFontSize = (text: string) => {
    const length = text.length;
    if (length < 50) return '1.75rem';      // Short quotes - larger text
    if (length < 100) return '1.5rem';     // Medium quotes
    if (length < 150) return '1.3rem';     // Longer quotes
    return '1.1rem';                       // Very long quotes - smaller text
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 2.5, 
        mb: 2.5, 
        height: '100%',
        minHeight: '161px', // Match UserGreeting exact height
        maxHeight: '161px', // Force exact height matching
        backgroundColor: theme.bg,
        background: `linear-gradient(145deg, ${theme.bg}, ${theme.bg}cc)`,
        backdropFilter: 'blur(10px)',
        color: '#fff',
        boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)',
        borderRadius: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        border: `1px solid ${theme.bg}66`,
        transition: 'all 0.3s ease',
        overflow: 'hidden' // Prevent content overflow
      }}
    >
      <Box sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        px: 2,
        py: 1
      }}>
        {/* Opening quote mark */}
        <Typography 
          variant="h3" 
          sx={{ 
            fontFamily: 'serif',
            position: 'absolute',
            top: '5%',
            left: '5%',
            color: theme.accent,
            fontSize: '2.5rem',
            opacity: 0.7,
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            lineHeight: 1
          }}
        >
          "
        </Typography>
        
        {/* Quote text - dynamically sized */}
        <Typography 
          variant="body1" 
          sx={{ 
            fontStyle: 'italic',
            fontSize: getQuoteFontSize(quote.quote),
            lineHeight: 1.4,
            textAlign: 'center',
            color: '#fff',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
            px: 3,
            py: 1,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3, // Limit to 3 lines max
            WebkitBoxOrient: 'vertical'
          }}
        >
          {quote.quote}
        </Typography>
        
        {/* Closing quote mark */}
        <Typography 
          variant="h3" 
          sx={{ 
            fontFamily: 'serif',
            position: 'absolute',
            bottom: '15%',
            right: '5%',
            color: theme.accent,
            fontSize: '2.5rem',
            opacity: 0.7,
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            lineHeight: 1
          }}
        >
          "
        </Typography>
      </Box>
      
      {/* Author attribution */}
      <Typography 
        variant="body2" 
        sx={{ 
          textAlign: 'right',
          fontSize: '0.9rem',
          opacity: 0.9,
          pr: 1,
          color: theme.accent,
          fontWeight: 400,
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
          mt: 'auto'
        }}
      >
        - {quote.author}
      </Typography>
    </Paper>
  );
};

export default QuoteCard;